import {
  checkboxClasses,
  formControlLabelClasses,
  typographyClasses,
} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {
  getV1CompaniesCompanyIdEquipmentTypesId,
  getV1CompaniesCompanyIdEquipmentTypesTypeahead,
  OrderUnitOfMeasure,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { CreateOptionChip } from '~components/FormFields/CreateOptionChip';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import NumberInput from '~components/FormFields/v2/NumberInput';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { NameIdSchemaRequired } from '~constants/regexConst';
import { useAllowedUnitOfMeasureOptions } from '~hooks/useAllowedUnitOfMeasureOptions';
import { CompanyBasic } from '~hooks/useCompany/models';
import {
  EquipmentTypeBasic,
  EquipmentTypeItem,
  EquipmentTypeTypeahead,
} from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { Order } from '~hooks/useOrders/models';
import { Project, ProjectBasic } from '~hooks/useProjects/models';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import {
  getStringOrNumberFieldPositiveNumberValue,
  nullableStringOrNumberIsValidNumber,
  numericFormValuesMatch,
} from '~utils/utilFunctions';

import { shouldEnableRecurringLoads } from '../../NewOrderForm/utils';

const getEquipmentGrossCapacity = (
  equipmentType: EquipmentTypeItem | EquipmentTypeBasic,
  uom: NameIdSchemaRequired | null,
  orderUnitOfMeasure?: NameIdSchemaRequired | null,
) => {
  const fullEquipmentType =
    equipmentType instanceof EquipmentTypeItem ? equipmentType : null;
  const isLoadsUoM = uom?.id === OrderUnitOfMeasure.LOAD;

  if (isLoadsUoM) {
    // When job UoM is LOAD, resolve capacity using the order-level UoM (e.g., Yard, Ton)
    const fallbackUoM =
      orderUnitOfMeasure?.id && orderUnitOfMeasure.id !== OrderUnitOfMeasure.LOAD
        ? orderUnitOfMeasure
        : null;

    if (fallbackUoM) {
      return getEquipmentGrossCapacity(equipmentType, fallbackUoM);
    }

    // No usable fallback -- use equipment's default capacity
    const grossCapacity = nullableStringOrNumberIsValidNumber(
      fullEquipmentType?.grossCapacity ?? null,
    )
      ? Number(fullEquipmentType?.grossCapacity)
      : null;
    return {
      grossCapacity,
      uom: fullEquipmentType?.grossCapacityUnitOfMeasure ?? null,
    };
  }

  const grossCapacity = nullableStringOrNumberIsValidNumber(
    fullEquipmentType?.grossCapacity ?? null,
  )
    ? Number(fullEquipmentType?.grossCapacity)
    : null;

  // no selected order uom -> use the default equipment capacity
  if (!uom?.id) {
    return { grossCapacity, uom: fullEquipmentType?.grossCapacityUnitOfMeasure ?? null };
  }

  // selected order uom -> attempt to match the order uom with the equipment default uom
  const selectedUoMMatchesEquipmentUoM =
    uom.id === fullEquipmentType?.grossCapacityUnitOfMeasure?.id;

  if (selectedUoMMatchesEquipmentUoM) {
    return { grossCapacity, uom };
  }

  // selected order uom and no default match -> check for an equipment additional capacity
  // entry with a uom matching the order uom
  const matchedAdditionalCapacity = fullEquipmentType?.additionalGrossCapacities.find(
    (capacity) => {
      return capacity.unitOfMeasure.id === uom.id;
    },
  );

  if (matchedAdditionalCapacity) {
    return { grossCapacity: Number(matchedAdditionalCapacity.capacity), uom };
  }

  // no match -> use the default equipment capacity
  return { grossCapacity, uom: fullEquipmentType?.grossCapacityUnitOfMeasure ?? null };
};

const calculateUnitsPerHour = (
  grossCapacity: number,
  cycleTimeInHours: number,
  truckCount: number,
) => {
  return Math.round((grossCapacity / cycleTimeInHours) * truckCount);
};

export type TrucksV2Dto = {
  company: CompanyBasic;
  project: ProjectBasic | null;
  jobUnitOfMeasure: NameIdSchemaRequired | null;
  truckCount: number | null;
  recurringLoads: boolean;
  loadsPerTruck: number | null;
  equipmentType: EquipmentTypeItem | EquipmentTypeBasic | null;
  equipmentTypeGrossCapacityUnitOfMeasure: NameIdSchemaRequired | null;
  equipmentTypeGrossCapacity: number | null;
  jobQuantity: number | null;
  deliveredQuantity: number | null;
  totalLoads: number | null;
  cycleTime: number | null;
  unitsPerHour: number | null;
};

interface TrucksProps {
  alwaysShowRecurringLoads?: boolean;
  hideSetCapacityAndUnitsHint?: boolean;
  hideTruckCount?: boolean;
  leftColumnBottomContent?: ReactNode;
  lockNonEditableFields?: boolean;
  order?: Order;
  parent?: Project;
  requireTruckCount?: boolean;
  // OrderFormV3 and ProjectFormV2 submit only dirty fields, so the values derived here
  // are dropped by the save unless they mark themselves dirty. Left off for
  // BulkEditJobDialog, which reads `dirtyFields.recurringLoads` as the user's intent to
  // apply that value to every selected job.
  shouldDirtyDerivedWrites?: boolean;
  tomlinsonOrderModalFeatureFlagEnabled?: boolean;
}

export default function Trucks({
  alwaysShowRecurringLoads,
  hideSetCapacityAndUnitsHint,
  hideTruckCount,
  leftColumnBottomContent,
  lockNonEditableFields,
  order,
  parent,
  requireTruckCount = true,
  shouldDirtyDerivedWrites = false,
  tomlinsonOrderModalFeatureFlagEnabled,
}: TrucksProps) {
  const { toasterStore, userStore } = useStores();

  const form = useFormContext<TrucksV2Dto>();
  const unitOfMeasureOptions = useAllowedUnitOfMeasureOptions();

  const [isFetching, setIsFetching] = useState(false);

  const [notFoundEquipmentTypeName, setNotFoundEquipmentTypeName] = useState('');
  const { createEquipmentTypeByCompanyId } = useEquipment();
  const [isCreatingEquipmentType, setIsCreatingEquipmentType] = useState(false);

  const companyId = form.watch('company.id');
  const dispatchAsCompanyId = getEffectiveUserCompanyId(userStore);
  const equipmentTypeCompanyId = companyId || dispatchAsCompanyId;

  const watchedJobUoM = form.watch('jobUnitOfMeasure');
  const watchedEquipmentTypeId = form.watch('equipmentType.id');
  const watchedQuantity = form.watch('jobQuantity') as string | number | null;
  const watchedEquipmentTypeGrossCapacity = form.watch('equipmentTypeGrossCapacity') as
    | string
    | number
    | null;
  const watchedOrderUoM = form.watch(
    'unitOfMeasure' as keyof TrucksV2Dto,
  ) as NameIdSchemaRequired | null;

  const isEditing = Boolean(order?.id);
  const { isDirty } = useFormState({ control: form.control });

  const matchedGrossCapacity = useMatchedGrossCapacityBasedOnJobUoM();

  const handleEquipmentTypeInput = useCallback(
    (_: React.SyntheticEvent, value: string) => {
      setNotFoundEquipmentTypeName(value);
    },
    [],
  );

  // A write that re-derives from the values the form mounted with is seeding the display,
  // so it travels with a save only once the user has edited something. Read through a ref
  // so the flag never joins an effect dep array: re-running a derive on the pristine →
  // dirty transition would let an edit to an unrelated field overwrite a saved value.
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const applyDerivedGrossCapacity = useCallback(
    (
      grossCapacity: string | number | null,
      unitOfMeasure: TrucksV2Dto['equipmentTypeGrossCapacityUnitOfMeasure'],
      shouldDirty = shouldDirtyDerivedWrites,
    ) => {
      if (
        !numericFormValuesMatch(
          form.getValues('equipmentTypeGrossCapacity'),
          grossCapacity,
        )
      ) {
        form.setValue('equipmentTypeGrossCapacity', (grossCapacity ?? '') as number, {
          shouldDirty,
        });
      }

      const currentUnitOfMeasure = form.getValues(
        'equipmentTypeGrossCapacityUnitOfMeasure',
      );

      if (currentUnitOfMeasure?.id !== unitOfMeasure?.id) {
        form.setValue('equipmentTypeGrossCapacityUnitOfMeasure', unitOfMeasure, {
          shouldDirty,
        });
      }
    },
    [shouldDirtyDerivedWrites],
  );

  const applyDerivedEstimate = useCallback(
    (name: 'totalLoads' | 'unitsPerHour', estimate: number | null) => {
      if (numericFormValuesMatch(form.getValues(name), estimate)) return;

      form.setValue(name, (estimate ?? '') as number, {
        shouldDirty: shouldDirtyDerivedWrites && isDirtyRef.current,
      });
    },
    [shouldDirtyDerivedWrites],
  );

  useEffect(() => {
    if (!isDirtyRef.current) return;
    if (watchedJobUoM?.id && watchedJobUoM?.id !== OrderUnitOfMeasure.LOAD) {
      const isRunning = shouldEnableRecurringLoads(
        watchedQuantity,
        watchedEquipmentTypeGrossCapacity,
        watchedJobUoM?.name,
      );

      if (Boolean(form.getValues('recurringLoads')) !== isRunning) {
        form.setValue('recurringLoads', isRunning, {
          shouldDirty: shouldDirtyDerivedWrites,
        });
      }
    }
  }, [
    shouldDirtyDerivedWrites,
    watchedJobUoM,
    watchedQuantity,
    watchedEquipmentTypeGrossCapacity,
  ]);

  const handleCreateEquipmentType = useCallback(async () => {
    setIsCreatingEquipmentType(true);
    try {
      const newEquipmentType = await createEquipmentTypeByCompanyId({
        companyId,
        equipmentType: {
          name: notFoundEquipmentTypeName,
          additionalGrossCapacities: [],
        } as unknown as EquipmentTypeItem,
      });

      form.setValue('equipmentType', newEquipmentType, {
        shouldValidate: true,
        shouldDirty: true,
      });

      toasterStore.push(
        alert(
          t('administration.equipment_type.created_message', {
            name: newEquipmentType.name,
          }),
          AlertTypes.success,
        ),
      );
    } catch {
      // Error is already handled in the hook
    } finally {
      setIsCreatingEquipmentType(false);
    }
  }, [companyId, notFoundEquipmentTypeName]);

  const handleEquipmentTypeChange = useCallback(
    async (selectedEquipmentType: EquipmentTypeItem) => {
      setIsFetching(true);

      const id = selectedEquipmentType.id;
      const response = await getV1CompaniesCompanyIdEquipmentTypesId({
        path: { 'company-id': equipmentTypeCompanyId, id },
      });
      const equipmentType = EquipmentTypeItem.parse(response.data.data);

      if (
        parent?.equipmentTypeGrossCapacity &&
        parent?.equipmentTypeGrossCapacityUnitOfMeasure
      ) {
        applyDerivedGrossCapacity(
          parent.equipmentTypeGrossCapacity,
          parent.equipmentTypeGrossCapacityUnitOfMeasure,
        );
      } else {
        // Use default equipment capacity calculation
        const grossCapacity = getEquipmentGrossCapacity(
          equipmentType,
          watchedJobUoM,
          watchedOrderUoM,
        );
        applyDerivedGrossCapacity(
          grossCapacity?.grossCapacity ?? '',
          grossCapacity?.uom ?? null,
        );
      }

      setIsFetching(false);

      return equipmentType;
    },
    [
      applyDerivedGrossCapacity,
      equipmentTypeCompanyId,
      parent?.equipmentTypeGrossCapacity,
      parent?.equipmentTypeGrossCapacityUnitOfMeasure,
      watchedJobUoM,
      watchedOrderUoM,
    ],
  );

  const handleEquipmentTypeGrossCapacityUnitOfMeasureChange = useCallback(
    async (selectedEquipmentTypeGrossCapacityUnitOfMeasure: ItemNameAndId | null) => {
      if (!watchedEquipmentTypeId) {
        return selectedEquipmentTypeGrossCapacityUnitOfMeasure;
      }

      setIsFetching(true);

      const response = await getV1CompaniesCompanyIdEquipmentTypesId({
        path: { 'company-id': equipmentTypeCompanyId, id: watchedEquipmentTypeId },
      });
      const equipmentType = EquipmentTypeItem.parse(response.data.data);
      const grossCapacity = getEquipmentGrossCapacity(
        equipmentType,
        selectedEquipmentTypeGrossCapacityUnitOfMeasure,
        watchedOrderUoM,
      );

      const equipmentCapacityMatchesSelectedUnitOfMeasure =
        grossCapacity.uom?.id === selectedEquipmentTypeGrossCapacityUnitOfMeasure?.id;

      applyDerivedGrossCapacity(
        equipmentCapacityMatchesSelectedUnitOfMeasure
          ? (grossCapacity?.grossCapacity ?? '')
          : '',
        selectedEquipmentTypeGrossCapacityUnitOfMeasure,
      );

      setIsFetching(false);

      return equipmentType;
    },
    [
      applyDerivedGrossCapacity,
      equipmentTypeCompanyId,
      watchedEquipmentTypeId,
      watchedOrderUoM,
    ],
  );

  const fetchEquipmentTypes = useCallback(
    async (additionalProps = { cursor: '', query: '' }) => {
      const EMPTY_RESULT = { data: [], pagination: undefined };

      // Without a company id the `{company-id}` path placeholder is left
      // unsubstituted by the generated client and the request 404s — the same guard
      // `useEquipmentTypesInfiniteQuery` applies to this endpoint. An order that has
      // no company on it yet reaches here.
      if (!companyId) return EMPTY_RESULT;

      try {
        const response = await getV1CompaniesCompanyIdEquipmentTypesTypeahead({
          path: { 'company-id': companyId },
          query: {
            'page[limit]': MAX_API_PAGE_SIZE,
            'page[after]': additionalProps.cursor || undefined,
            'search[query]': additionalProps.query?.trim() || undefined,
          },
        });

        return {
          data: response.data.data.map((item) => EquipmentTypeTypeahead.parse(item)),
          pagination: extractPagination(response),
        };
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.equipment_types.failed_to_fetch'),
          );
        } catch {
          // Error already handled (toast + Sentry)
        }

        return EMPTY_RESULT;
      }
    },
    [companyId],
  );

  const applyEquipmentTypeFromProject = useCallback(() => {
    const equipmentTypeIdFromProject = parent?.equipmentType?.id;
    const grossCapacityFromProject = parent?.equipmentTypeGrossCapacity;

    if (equipmentTypeIdFromProject) {
      setIsFetching(true);

      getV1CompaniesCompanyIdEquipmentTypesId({
        path: { 'company-id': equipmentTypeCompanyId, id: equipmentTypeIdFromProject },
      })
        .then((response) => {
          const equipmentType = EquipmentTypeItem.parse(response.data.data);
          // The project form mounts without an `order`, so this cascade also runs on open
          // for an existing project — where the equipment type's own capacity unit fills
          // in for a project that has none. Only a project the user picked may dirty.
          const shouldDirty = shouldDirtyDerivedWrites && isDirtyRef.current;

          const storedEquipmentType = form.getValues('equipmentType');
          const equipmentTypeChanged = storedEquipmentType?.id !== equipmentType.id;
          // Swapping the order's nested variant for this full record is not an edit —
          // same entity, more data — but it is what lets a later unit change derive a
          // capacity, so it happens whether or not the entity changed.
          if (
            equipmentTypeChanged ||
            !(storedEquipmentType instanceof EquipmentTypeItem)
          ) {
            form.setValue('equipmentType', equipmentType, {
              shouldDirty: shouldDirty && equipmentTypeChanged,
            });
          }

          // Capacity and unit fall back together, the way the equipment-type selector
          // resolves them: the project's pair wins, otherwise the equipment type's own.
          applyDerivedGrossCapacity(
            grossCapacityFromProject || equipmentType.grossCapacity || '',
            parent.equipmentTypeGrossCapacityUnitOfMeasure ??
              equipmentType.grossCapacityUnitOfMeasure ??
              null,
            shouldDirty,
          );
        })
        .finally(() => setIsFetching(false));
    }
  }, [
    applyDerivedGrossCapacity,
    equipmentTypeCompanyId,
    parent?.equipmentType?.id,
    parent?.equipmentTypeGrossCapacity,
    parent?.equipmentTypeGrossCapacityUnitOfMeasure?.id,
    shouldDirtyDerivedWrites,
  ]);

  // when creating an order, grab the equipment type set on the selected project
  useEffect(() => {
    if (!isEditing) {
      applyEquipmentTypeFromProject();
    }
  }, [isEditing, applyEquipmentTypeFromProject]);

  // when editing an order, we should wait until the order's project (if any) is loaded. this is to
  // prevent the project's equipment type gross capacity (if any) from overriding the gross
  // capacity on the order.
  const isProjectLoaded = useNotifyWhenProjectIsLoaded({ isEditing, project: parent });
  const shouldApplyEquipmentTypeFromProject = useRef(false);

  useEffect(() => {
    if (isEditing && isProjectLoaded) {
      if (!shouldApplyEquipmentTypeFromProject.current) {
        shouldApplyEquipmentTypeFromProject.current = true;

        return;
      }

      applyEquipmentTypeFromProject();
    }
  }, [isEditing, isProjectLoaded, applyEquipmentTypeFromProject]);

  useEffect(() => {
    if (matchedGrossCapacity) {
      applyDerivedGrossCapacity(
        matchedGrossCapacity.grossCapacity ?? '',
        matchedGrossCapacity.uom,
      );
    }
  }, [applyDerivedGrossCapacity, matchedGrossCapacity]);

  const estimatedDeliveredQuantity = useEstimatedDeliveredQuantity();
  useEffect(() => {
    form.setValue('deliveredQuantity', (estimatedDeliveredQuantity ?? '') as number, {
      shouldDirty: false,
    });
  }, [estimatedDeliveredQuantity]);

  const estimatedTotalLoads = useEstimatedTotalLoads();
  useEffect(() => {
    applyDerivedEstimate('totalLoads', estimatedTotalLoads);
  }, [applyDerivedEstimate, estimatedTotalLoads]);

  const estimatedUnitsPerHour = useEstimatedUnitsPerHour();
  useEffect(() => {
    applyDerivedEstimate('unitsPerHour', estimatedUnitsPerHour);
  }, [applyDerivedEstimate, estimatedUnitsPerHour]);

  return (
    <Grid container spacing={2}>
      {!hideTruckCount && (
        <Grid size={{ xs: 4 }}>
          <NumberInput
            control={form.control}
            dataUi="page-sales-orders__order-form-v2__truck-count-input"
            isRequired={requireTruckCount}
            label={t('form_fields.number_of_trucks')}
            name="truckCount"
          />
        </Grid>
      )}
      <Grid size={{ xs: hideTruckCount ? 6 : 3 }}>
        <AutocompleteFormField
          clearable
          control={form.control}
          dataUi="page-sales-orders__order-form-v2__job-unit-of-measure-input"
          disabled={lockNonEditableFields}
          errors={form.formState.errors}
          getLabel={(item) => item?.name ?? ''}
          getValue={(item) => item?.id ?? ''}
          label={`${t('form_fields.units')}`}
          list={unitOfMeasureOptions}
          name="jobUnitOfMeasure"
        />
      </Grid>
      <Grid size={{ xs: hideTruckCount ? 4 : 3 }}>
        <NumberInput
          control={form.control}
          slotProps={{ htmlInput: { step: '0.01' } }}
          isDisabled={lockNonEditableFields}
          isRequired={
            !isNil(watchedJobUoM) && watchedJobUoM?.id !== OrderUnitOfMeasure.LOAD
          }
          label={t('form_fields.quantity')}
          name="jobQuantity"
        />
      </Grid>

      {(alwaysShowRecurringLoads || watchedJobUoM?.id === OrderUnitOfMeasure.LOAD) && (
        <Grid size={{ xs: 2 }}>
          <CheckboxInput
            control={form.control}
            isDisabled={lockNonEditableFields}
            label={`${t('common.running')}`}
            name="recurringLoads"
            sx={{
              mt: '14px',
              [`& .${formControlLabelClasses.root}`]: { mr: 0 },
              [`& .${checkboxClasses.root}`]: { pr: '2px' },
              [`& .${typographyClasses.root}`]: { fontSize: '12px', fontWeight: 400 },
            }}
          />
        </Grid>
      )}
      <Grid size={{ xs: 6 }}>
        <AutocompleteAsyncFormField
          asyncCallback={fetchEquipmentTypes}
          control={form.control}
          disabled={lockNonEditableFields}
          errors={form.formState.errors}
          getLabel={(item) => item?.name ?? ''}
          getValue={(item) => item?.id ?? ''}
          isRequired={tomlinsonOrderModalFeatureFlagEnabled}
          label={`${t('dispatch.order.order_details_v2.truck_type')}`}
          name="equipmentType"
          noOptionsText={
            notFoundEquipmentTypeName ? (
              <CreateOptionChip
                disabled={isCreatingEquipmentType}
                name={notFoundEquipmentTypeName}
                onClick={handleCreateEquipmentType}
              />
            ) : (
              t('form_fields.no_options')
            )
          }
          onInput={handleEquipmentTypeInput}
          onSelect={handleEquipmentTypeChange}
          clearable
        />

        {leftColumnBottomContent && <Box sx={{ mt: 4 }}>{leftColumnBottomContent}</Box>}
      </Grid>
      <Grid size={{ xs: 6 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 7 }}>
            <AutocompleteFormField
              clearable
              control={form.control}
              disabled={isFetching || lockNonEditableFields}
              errors={form.formState.errors}
              getLabel={(item) => item?.name ?? ''}
              getValue={(item) => item?.id ?? ''}
              label={`${t('form_fields.units')}`}
              list={unitOfMeasureOptions}
              name="equipmentTypeGrossCapacityUnitOfMeasure"
              onSelect={handleEquipmentTypeGrossCapacityUnitOfMeasureChange}
            />
          </Grid>

          <Grid size={{ xs: 5 }}>
            <NumberInput
              control={form.control}
              isDisabled={isFetching || lockNonEditableFields}
              label={t('dispatch.order.order_details_v2.truck_capacity')}
              name="equipmentTypeGrossCapacity"
            />
          </Grid>

          {!hideSetCapacityAndUnitsHint && (
            <Typography
              sx={(theme) => ({
                color: theme.brandV2.colors.treadGray1,
                flexGrow: 1,
                fontSize: '10px',
                mt: -1.5,
                textAlign: 'right',
              })}
            >
              {t('order.form_v2.set_capacity_and_units')}
            </Typography>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
}

function useEstimatedUnitsPerHour() {
  const form = useFormContext<TrucksV2Dto>();

  const watchedCycleTime = form.watch('cycleTime');
  const watchedGrossCapacity = form.watch('equipmentTypeGrossCapacity');
  const watchedTruckCount = form.watch('truckCount');

  return useMemo(() => {
    const cycleTimeInMinutes =
      getStringOrNumberFieldPositiveNumberValue(watchedCycleTime);
    const cycleTimeInHours = isNil(cycleTimeInMinutes) ? null : cycleTimeInMinutes / 60;
    const grossCapacity = getStringOrNumberFieldPositiveNumberValue(watchedGrossCapacity);
    const truckCount = getStringOrNumberFieldPositiveNumberValue(watchedTruckCount);

    if (
      isNil(truckCount) ||
      isNil(grossCapacity) ||
      isNil(cycleTimeInHours) ||
      cycleTimeInHours <= 0
    ) {
      return null;
    }

    return calculateUnitsPerHour(grossCapacity, cycleTimeInHours, truckCount);
  }, [watchedGrossCapacity, watchedCycleTime, watchedTruckCount]);
}

function useEstimatedTotalLoads() {
  const form = useFormContext<TrucksV2Dto>();

  const watchedTruckCount = form.watch('truckCount');
  const watchedJobQuantity = form.watch('jobQuantity');
  const watchedJobUoM = form.watch('jobUnitOfMeasure');

  // A cleared number input holds '', which multiplies to 0 — a real load count the form
  // would then save. Both operands go through the positive-number read so a blank one
  // leaves the estimate unknown.
  const jobQuantity = getStringOrNumberFieldPositiveNumberValue(watchedJobQuantity);
  const truckCount = getStringOrNumberFieldPositiveNumberValue(watchedTruckCount);

  if (
    watchedJobUoM?.id === OrderUnitOfMeasure.LOAD &&
    !isNil(jobQuantity) &&
    !isNil(truckCount)
  ) {
    return jobQuantity * truckCount;
  }

  return null;
}

function useEstimatedDeliveredQuantity() {
  const form = useFormContext<TrucksV2Dto>();

  const watchedTruckCount = form.watch('truckCount');
  const watchedGrossCapacity = form.watch('equipmentTypeGrossCapacity');
  const watchedJobUoM = form.watch('jobUnitOfMeasure');
  const watchedJobQuantity = form.watch('jobQuantity');

  if (
    watchedJobUoM?.id === OrderUnitOfMeasure.LOAD &&
    !isNil(watchedGrossCapacity) &&
    !isNil(watchedTruckCount) &&
    !isNil(watchedJobQuantity)
  ) {
    return watchedJobQuantity * watchedGrossCapacity * watchedTruckCount;
  }

  if (
    watchedJobUoM?.id !== OrderUnitOfMeasure.LOAD &&
    !isNil(watchedJobQuantity) &&
    !isNil(watchedTruckCount)
  ) {
    return watchedTruckCount * watchedJobQuantity;
  }

  return null;
}

/**
 * Listen to changes on the job's unit of measure and derive the equipment's gross
 * capacity & unit of measure.
 */
function useMatchedGrossCapacityBasedOnJobUoM() {
  const [match, setMatch] = useState<{
    grossCapacity: number | null;
    uom: NameIdSchemaRequired | null;
  }>();

  const form = useFormContext<TrucksV2Dto>();
  const selectedEquipmentType = form.watch('equipmentType');

  const selectedUoM = form.watch('jobUnitOfMeasure');
  const watchedOrderUoM = form.watch(
    'unitOfMeasure' as keyof TrucksV2Dto,
  ) as NameIdSchemaRequired | null;

  const previousJobUoMId = useRef(selectedUoM?.id);
  const previousOrderUoMId = useRef(watchedOrderUoM?.id);

  useEffect(() => {
    const jobUoMChanged =
      !isNil(previousJobUoMId.current) && previousJobUoMId.current !== selectedUoM?.id;
    const orderUoMChanged =
      !isNil(previousOrderUoMId.current) &&
      previousOrderUoMId.current !== watchedOrderUoM?.id;

    previousJobUoMId.current = selectedUoM?.id;
    previousOrderUoMId.current = watchedOrderUoM?.id;

    // An edit form seeds the equipment type from the order, which carries the nested
    // variant — it holds no capacity data, so deriving from it yields a blank capacity
    // that would erase the saved one. Wait for the typeahead to supply the full record.
    const equipmentTypeCarriesCapacities =
      selectedEquipmentType instanceof EquipmentTypeItem;

    if ((jobUoMChanged || orderUoMChanged) && equipmentTypeCarriesCapacities) {
      const grossCapacity = getEquipmentGrossCapacity(
        selectedEquipmentType,
        selectedUoM,
        watchedOrderUoM,
      );

      setMatch({
        grossCapacity: (grossCapacity?.grossCapacity ?? '') as number,
        uom: grossCapacity?.uom ?? null,
      });
    }
  }, [selectedUoM?.id, watchedOrderUoM?.id, selectedEquipmentType]);

  return match;
}

/**
 * When selecting a project, notify once it is loaded so we can start deriving values from it
 */
interface UseNotifyWhenProjectIsLoadedProps {
  isEditing: boolean;
  project?: ProjectBasic;
}

function useNotifyWhenProjectIsLoaded({
  isEditing,
  project,
}: UseNotifyWhenProjectIsLoadedProps) {
  const form = useFormContext<TrucksV2Dto>();
  const selectedProjectId = form.watch('project.id');

  const initialProjectId = useRef(selectedProjectId).current;
  const isProjectLoaded = useRef(isEditing && selectedProjectId ? false : true);

  if (!isProjectLoaded.current) {
    isProjectLoaded.current = initialProjectId === project?.id;
  }

  const [isLoaded, setIsLoaded] = useState(
    isEditing && selectedProjectId ? isProjectLoaded.current : true,
  );

  useEffect(() => {
    if (isEditing && selectedProjectId && isProjectLoaded.current) {
      setIsLoaded(true);
    }
  }, [isEditing, selectedProjectId, isProjectLoaded.current]);

  return isLoaded;
}
